/**
 * Browser-side identity keypair (`O_pk`, `O_sk`) for the dashboard's
 * end-to-end decryption flow. Plan §3.4 (rewritten).
 *
 * The keypair is X25519. We use `tweetnacl.box.keyPair()` to generate it
 * because it's already a dep (used by client-web's age envelopes) and
 * provides identical math to libsodium's `crypto_box_keypair`. WebCrypto
 * doesn't yet expose X25519 across all Chromium versions — using nacl
 * keeps the surface stable.
 *
 * The private bytes (32 bytes) are wrapped to a passphrase- or
 * authenticator-derived 32-byte secret via WebCrypto AES-256-GCM. The
 * resulting blob is opaque to the server — only re-derivation of the
 * wrapping secret can recover `O_sk`.
 *
 * Wire format of `wrappedBlob`: `nonce(12) ‖ ciphertext`. Encoded as
 * base64-standard when shipped over JSON.
 */

import nacl from "tweetnacl";
import naclUtil from "tweetnacl-util";

const NONCE_BYTES = 12;
const KEY_BITS = 256;

/** Hex-encoded 32-byte X25519 public key. 64 lowercase hex chars. */
export type Hex32 = string;

export interface BrowserKeypair {
  /** Lowercase hex of the 32-byte X25519 public half. Bound by the cert. */
  publicKeyHex: Hex32;
  /** 32 raw bytes of the X25519 private half. Caller MUST wrap before
   * persisting and zero-fill the array as soon as it's no longer
   * needed. */
  secretKeyBytes: Uint8Array;
}

/** Generate a fresh X25519 keypair via tweetnacl. */
export function generateKeypair(): BrowserKeypair {
  const kp = nacl.box.keyPair();
  return {
    publicKeyHex: bytesToHex(kp.publicKey),
    secretKeyBytes: kp.secretKey,
  };
}

/** Lowercase-hex encoder. */
export function bytesToHex(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 1) {
    s += bytes[i].toString(16).padStart(2, "0");
  }
  return s;
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new Error("hex string must be even-length");
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/**
 * Wrap raw private-key bytes to a 32-byte secret using AES-256-GCM.
 * Returns `nonce ‖ ciphertext` as a single buffer.
 *
 * The `wrappingSecret` MUST be 32 bytes. Callers derive it from a
 * passphrase via `derivePassphraseSecret` or a WebAuthn `prf` evaluation.
 */
export async function wrapSecretKey(
  secretKeyBytes: Uint8Array,
  wrappingSecret: Uint8Array,
): Promise<Uint8Array> {
  if (wrappingSecret.length !== 32) {
    throw new Error(`wrappingSecret must be 32 bytes, got ${wrappingSecret.length}`);
  }
  const key = await crypto.subtle.importKey(
    "raw",
    wrappingSecret.slice().buffer,
    { name: "AES-GCM", length: KEY_BITS },
    false,
    ["encrypt", "decrypt"],
  );
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
  // Re-pack into a fresh ArrayBuffer to avoid the SharedArrayBuffer
  // narrowing issue some bundlers' DOM types impose.
  const skBuf = secretKeyBytes.slice().buffer;
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce }, key, skBuf),
  );
  const out = new Uint8Array(NONCE_BYTES + ciphertext.length);
  out.set(nonce, 0);
  out.set(ciphertext, NONCE_BYTES);
  return out;
}

/**
 * Unwrap a wrapped-key blob produced by `wrapSecretKey`. Throws on bad
 * wrapping secret (AES-GCM tag mismatch).
 */
export async function unwrapSecretKey(
  wrappedBlob: Uint8Array,
  wrappingSecret: Uint8Array,
): Promise<Uint8Array> {
  if (wrappingSecret.length !== 32) {
    throw new Error(`wrappingSecret must be 32 bytes, got ${wrappingSecret.length}`);
  }
  if (wrappedBlob.length < NONCE_BYTES + 16) {
    throw new Error("wrapped blob is too short");
  }
  const nonce = wrappedBlob.slice(0, NONCE_BYTES);
  const ciphertext = wrappedBlob.slice(NONCE_BYTES);
  const key = await crypto.subtle.importKey(
    "raw",
    wrappingSecret.slice().buffer,
    { name: "AES-GCM", length: KEY_BITS },
    false,
    ["encrypt", "decrypt"],
  );
  const ctBuf = ciphertext.slice().buffer;
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonce },
    key,
    ctBuf,
  );
  const out = new Uint8Array(plaintext);
  if (out.length !== 32) {
    throw new Error(`unwrapped key must be 32 bytes, got ${out.length}`);
  }
  return out;
}

/** Convenience encoders used by the dashboard's transport layer. */
export const base64 = {
  encode(bytes: Uint8Array): string {
    return naclUtil.encodeBase64(bytes);
  },
  decode(s: string): Uint8Array {
    return naclUtil.decodeBase64(s);
  },
};

/** Zero-fill a key buffer in place once it's no longer needed. Defensive
 * — JS GC may already overwrite the bytes, but explicit zeroing is
 * standard hygiene for short-lived secret material. */
export function zero(bytes: Uint8Array): void {
  bytes.fill(0);
}
